import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock, MapPin, Quote } from "lucide-react";
import { Badge, Button, Card, CardContent, Hero, Section } from "@alight/ui";
import { useSeo } from "../lib/seo";
import { locationNames, locationShortList, useBlock } from "../lib/content";
import { seasonStartLabel } from "../lib/season";
import { CmsText } from "../components/CmsText";
import { JsonLd } from "../components/JsonLd";
import { organizationJsonLd, websiteJsonLd } from "../lib/structured-data";

export const Route = createFileRoute("/")({
  component: HomePage,
});

/** A CMS link is either a route on this site ("/sessions") or a full URL. */
function CmsLink({ href, children, className }: { href: string; children: React.ReactNode; className?: string }) {
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

function HomePage() {
  const hero = useBlock("hero");
  const home = useBlock("home");
  const season = useBlock("season");
  const locations = useBlock("locations");
  const testimonials = useBlock("testimonials");
  const contact = useBlock("contact");
  const startLabel = seasonStartLabel(season.sessions);

  useSeo({
    title: "Connect Sydney — Jesus | People | Campus",
    description: `Join Connect Sydney — a free ministry for Year 12 leavers, connecting you with Jesus, new friends and your university Christian group. Mondays ${season.time} after the HSC, at ${locationNames(locations.items)}.`,
    path: "/",
    image: hero.image,
  });

  return (
    <>
      <JsonLd data={[organizationJsonLd(contact), websiteJsonLd()]} />
      <Hero
        eyebrow={hero.eyebrow}
        title={hero.title}
        description={
          <>
            <span className="block font-heading text-xl font-bold uppercase tracking-[0.2em] text-hero-eyebrow">
              {hero.tagline}
            </span>
            <span className="mt-4 block">{hero.intro}</span>
          </>
        }
        actions={
          <>
            <Button asChild variant="accent" size="lg">
              <CmsLink href={hero.primaryHref}>
                {hero.primaryLabel}
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
              </CmsLink>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-hero-fg/40 text-hero-fg hover:bg-hero-fg/10 hover:text-hero-fg"
            >
              <CmsLink href={hero.secondaryHref}>{hero.secondaryLabel}</CmsLink>
            </Button>
          </>
        }
        backgroundMedia={
          // The LCP element on the home page. See PageIntro.tsx for why this
          // is spelled as the lowercase HTML attribute, not the React prop.
          <img
            src={hero.image}
            alt={hero.imageAlt}
            width={1800}
            height={900}
            loading="eager"
            // @ts-expect-error -- fetchpriority isn't in React 18's img prop types
            fetchpriority="high"
          />
        }
      />

      {/* This season at a glance — the one strip every grad is looking for. */}
      <Section tone="card" spacing="sm">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold text-ink">
            <Badge variant="accent">Connect {season.name}</Badge>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-primary" aria-hidden />
              {startLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-primary" aria-hidden />
              Mondays {season.time}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-primary" aria-hidden />
              {locationShortList(locations.items)}
            </span>
          </div>
          <Button asChild variant="outline" size="sm" className="shrink-0">
            <Link to="/sessions">All the dates</Link>
          </Button>
        </div>
      </Section>

      {/* What is Connect? */}
      <Section tone="paper" spacing="lg">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <img
            src={home.whatImage}
            alt={home.whatImageAlt}
            width={1200}
            height={680}
            loading="lazy"
            className="w-full rounded-card object-cover shadow-card"
          />
          <div>
            <h2 className="font-heading text-3xl font-extrabold text-ink">{home.whatTitle}</h2>
            <p className="mt-4 text-lg text-muted">{home.whatIntro}</p>
            <ul className="mt-6 space-y-3">
              {home.points.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary/25 font-heading text-xs font-bold text-primary"
                  >
                    {i + 1}
                  </span>
                  <span className="text-ink">
                    <strong className="font-semibold text-primary">{p.label}</strong>{" "}
                    <span className="text-muted">{p.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/what-happens">What happens at Connect?</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/uni-christian-groups">Uni Christian groups</Link>
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Affiliations */}
      <Section tone="card" spacing="lg">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="lg:order-2">
            <img
              src={home.affiliationsImage}
              alt={home.affiliationsImageAlt}
              width={1200}
              height={800}
              loading="lazy"
              className="w-full rounded-card object-cover shadow-card"
            />
          </div>
          <div className="lg:order-1">
            <h2 className="font-heading text-3xl font-extrabold text-ink">{home.affiliationsTitle}</h2>
            <p className="mt-4 text-lg text-muted">{home.affiliationsIntro}</p>
            <p className="mt-4 leading-relaxed text-ink">
              <strong className="font-semibold">This includes:</strong> {home.affiliationsList}
            </p>
            <div className="mt-8">
              <Button asChild variant="outline">
                <Link to="/uni-christian-groups">More on uni Christian groups</Link>
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* History */}
      <Section tone="paper" spacing="lg">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <img
            src={home.historyImage}
            alt={home.historyImageAlt}
            width={1200}
            height={720}
            loading="lazy"
            className="w-full rounded-card object-cover shadow-card"
          />
          <div>
            <h2 className="font-heading text-3xl font-extrabold text-ink">{home.historyTitle}</h2>
            <div className="mt-4 space-y-4 text-lg leading-relaxed text-muted">
              <CmsText text={home.historyBody} />
            </div>
          </div>
        </div>
      </Section>

      {/* Featured testimonial */}
      <Section tone="primary" spacing="lg">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center font-heading text-3xl font-extrabold">Testimonials</h2>
          <Card className="mt-10 border-0 bg-primary-fg/10 text-primary-fg shadow-none">
            <CardContent className="grid gap-6 p-6 sm:grid-cols-[auto_1fr] sm:p-8">
              <img
                src={testimonials.featuredImage}
                alt={testimonials.featuredName}
                width={171}
                height={228}
                loading="lazy"
                className="mx-auto h-40 w-32 rounded-card object-cover sm:mx-0"
              />
              <div>
                <Quote className="h-8 w-8 text-accent" aria-hidden />
                <p className="mt-3 text-lg leading-relaxed">{testimonials.featuredQuote}</p>
                <p className="mt-5 font-heading text-lg font-bold">{testimonials.featuredName}</p>
                <p className="text-sm text-primary-fg/75">{testimonials.featuredRole}</p>
              </div>
            </CardContent>
          </Card>
          <div className="mt-8 text-center">
            <Button asChild variant="accent">
              <Link to="/testimonials">Read testimonials from students</Link>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
